if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-sticky-add-button]');
        this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option-select]'));
        this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]');
        this.button?.addEventListener('click', this.onButtonClick.bind(this));
        this.optionSelects.forEach((select) => select.addEventListener('change', this.onOptionChange.bind(this)));
      }

      connectedCallback() {
        this.bindProductElements();
        this.sync();
        this.observeMainButton();
        this.observeProductChanges();
        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.sectionId || !data.variant) return;
            this.syncOptionSelects(data.variant.id);
          });
        }
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      bindProductElements() {
        this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainPrice = document.getElementById(`price-${this.sectionId}`);
        this.stickyPrice = this.querySelector('.sticky-add-to-cart__price');
        this.variantPicker = document.getElementById(`variant-selects-${this.sectionId}`);
      }

      observeMainButton() {
        if (!this.mainButton) return;

        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          const hasScrolledPastButton = entry.boundingClientRect.top < 0;
          this.toggleAttribute('hidden', entry.isIntersecting || !hasScrolledPastButton);
        });
        this.intersectionObserver.observe(this.mainButton);
      }

      observeProductChanges() {
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();

        if (this.mainButton) {
          this.buttonObserver = new MutationObserver(() => this.syncButton());
          this.buttonObserver.observe(this.mainButton, {
            attributes: true,
            childList: true,
            subtree: true,
          });
        }

        if (this.mainPrice) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.mainPrice, {
            childList: true,
            subtree: true,
          });
        }
      }

      onButtonClick() {
        if (!this.mainButton || this.button?.disabled) return;
        this.mainButton.click();
      }

      onOptionChange() {
        const optionValues = this.optionSelects.map((select) => select.value);
        this.updateMainVariantPicker(optionValues);
      }

      updateMainVariantPicker(optionValues) {
        if (!this.variantPicker) return;

        let lastUpdatedInput = null;

        optionValues.forEach((optionValue, index) => {
          const select = this.variantPicker.querySelectorAll('select[name^="options["]')[index];
          if (select) {
            select.value = optionValue;
            lastUpdatedInput = select;
            return;
          }

          const fieldsets = this.variantPicker.querySelectorAll('fieldset');
          const input = Array.from(fieldsets[index]?.querySelectorAll('input') || []).find(
            (fieldInput) => fieldInput.value === optionValue
          );
          if (input) {
            input.checked = true;
            lastUpdatedInput = input;
          }
        });

        lastUpdatedInput?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      sync() {
        this.syncButton();
        this.syncPrice();
        this.syncOptionSelects(this.productFormVariantId);
      }

      syncButton() {
        if (!this.button || !this.mainButton) return;

        const mainButtonText = this.mainButton.querySelector('span')?.textContent?.trim();
        const stickyButtonText = this.button.querySelector('span');
        const isDisabled = this.mainButton.hasAttribute('disabled') || this.mainButton.getAttribute('aria-disabled') === 'true';

        this.button.disabled = isDisabled;
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        if (mainButtonText && stickyButtonText) stickyButtonText.textContent = mainButtonText;
      }

      syncPrice() {
        if (!this.mainPrice || !this.stickyPrice) return;

        const price = this.mainPrice.querySelector('.price');
        if (price) this.stickyPrice.innerHTML = price.outerHTML;
      }

      syncOptionSelects(variantId) {
        if (!this.optionSelects.length || !variantId) return;

        const variant = this.variants.find((item) => item.id.toString() === variantId.toString());
        if (!variant) return;

        variant.options.forEach((optionValue, index) => {
          if (this.optionSelects[index]) this.optionSelects[index].value = optionValue;
        });
      }

      get productFormVariantId() {
        return document.querySelector(`#product-form-${this.sectionId} input[name="id"]`)?.value;
      }
    }
  );
}
